import { motion } from 'framer-motion';
import { translations } from '../locales/translations';

function Worship({ lang = 'ko' }) {
  const t = translations[lang];
  
  const worshipSchedule = [
    {
      name: { ko: '주일예배', en: 'Sunday Service' },
      emoji: '🌅',
      time: '11:00 AM',
      location: { ko: '본당', en: 'Main Hall' },
      description: { 
        ko: '온 가족이 함께하는 주일 낮 예배',
        en: 'Family Sunday Morning Service'
      }
    },
    {
      name: { ko: '주일오후예배', en: 'Sunday Evening Service' },
      emoji: '🌆',
      time: '2:00 PM',
      location: { ko: '본당', en: 'Main Hall' },
      description: { 
        ko: '말씀과 찬양으로 드리는 주일 오후 예배',
        en: 'Sunday Evening Worship & Praise'
      }
    },
    {
      name: { ko: '수요예배', en: 'Wednesday Service' },
      emoji: '✝️',
      time: '7:30 PM',
      location: { ko: '본당', en: 'Main Hall' },
      description: { 
        ko: '말씀과 기도로 함께하는 수요예배',
        en: 'Midweek Bible Study & Prayer'
      }
    },
    {
      name: { ko: '새벽기도', en: 'Morning Prayer' },
      emoji: '🌄',
      time: '5:30 AM',
      location: { ko: '본당', en: 'Main Hall' },
      description: { 
        ko: '매일 드리는 새벽기도회 (월-금)',
        en: 'Daily Morning Prayer Service (Mon-Fri)'
      }
    },
    {
      name: { ko: '금요기도회', en: 'Friday Service' },
      emoji: '🕊️',
      time: '8:00 PM',
      location: { ko: '본당', en: 'Main Hall' },
      description: { 
        ko: '찬양과 기도로 함께하는 성령집회',
        en: 'Praise & Prayer Night'
      }
    }
  ];

  return (
    <div className="min-h-screen pt-20 pb-16 px-4 bg-primary/10">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            ⛪️ {lang === 'ko' ? '예배 시간 안내' : 'Worship Schedule'}
          </h1>
          <p className="text-lg text-gray-600">
            {lang === 'ko' 
              ? '한소망교회와 함께 예배드리시기를 환영합니다' 
              : 'Welcome to worship with HanSomang Church'}
          </p>
        </motion.div>

        <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-green-50 to-blue-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                    🙏 {lang === 'ko' ? '예배' : 'Service'}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                    ⏰ {lang === 'ko' ? '시간' : 'Time'}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                    📍 {lang === 'ko' ? '장소' : 'Location'}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                    📝 {lang === 'ko' ? '설명' : 'Description'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {worshipSchedule.map((service, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="hover:bg-gray-50 transition-colors duration-200"
                  >
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-xl mr-2">{service.emoji}</span>
                        <span className="text-sm font-medium text-gray-900">{service.name[lang]}</span>
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-600">
                      {service.time}
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-600">
                      {service.location[lang]}
                    </td>
                    <td className="px-6 py-5 text-sm text-gray-600">
                      {service.description[lang]}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 p-6 bg-white rounded-xl shadow-lg border border-gray-100"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <span className="text-2xl mr-2">📢</span>
            {lang === 'ko' ? '예배 안내' : 'Worship Information'}
          </h2>
          <ul className="space-y-3 text-gray-600">
            <li className="flex items-center">
              <span className="text-lg mr-2">🎥</span>
              {lang === 'ko' 
                ? '모든 예배는 현장예배와 온라인으로 동시에 진행됩니다.' 
                : 'All services are available both in-person and online.'}
            </li>
            <li className="flex items-center">
              <span className="text-lg mr-2">👨‍👩‍👧‍👦</span>
              {lang === 'ko'
                ? '주일예배는 온가족이 함께 드리는 예배입니다.'
                : 'Sunday service is a family worship service.'}
            </li>
            <li className="flex items-center">
              <span className="text-lg mr-2">🚌</span>
              {lang === 'ko'
                ? '차량운행이 필요하신 분들은 미리 연락주시기 바랍니다.'
                : 'Please contact us in advance if you need transportation.'}
            </li>
          </ul>
        </motion.div>
      </div>
    </div>
  );
}

export default Worship;